<template>
    <div ref="outerElementRef" class="devmode-wrapper">
        <div
            v-show="showArrows && !isUpButtonHidden"
            ref="arrowUp"
            class="devmode__arrow devmode__arrow_up"
            @click="clickUp"
        > 
            <common-icon :icon-name="'IconArrow'" />
        </div>
        <div
            ref="scrollable"
            class="devmode-viewport"
        >
            <div
                v-for="(section, sectionIdx) in listItems"
                :key="section.key"
                class="devmode-section"
            >
                <h5 class="devmode-section__title">
                    {{ $t(section.title) }}
                </h5>
                <p class="devmode-section__description">
                    {{ $t(section.description) }}
                </p>
                <div class="devmode__items">
                    <button
                        v-for="(item, idx) in section.values"
                        :id="getId(section.key, item.value)"
                        :ref="getId(section.key, item.value)"
                        :key="item.value"
                        class="devmode__item"
                        @click="() => setSettings(section.key, item.value)"
                        @focus="onFocus(sectionIdx, idx)"
                        @blur="onBlur"
                    >
                        <div>
                            {{ $t(item.title) }}
                        </div>
                        <common-icon v-if="isChecked(section.key, item.value)" :icon-name="'IconCheck'" /> 
                    </button>
                </div>
            </div>
            <!-- RESET SECTION -->
            <div
                class="devmode-section"
            >
                <h5 class="devmode-section__title">
                    {{ resetTitle }}
                </h5>
                <p class="devmode-section__description">
                    {{ resetDescription }}
                </p>
                <div class="devmode__items">
                    <button
                        :id="getId('reset', 'reset')"
                        :ref="getId('reset', 'reset')"
                        class="devmode__item"
                        @click="deactivateDevMode()"
                        @focus="onFocus()"
                        @blur="onBlur"
                    >
                        {{ resetButton }}
                    </button>
                </div>
            </div>
            <!-- RESET SECTION -->
        </div>
        <div
            v-show="showArrows && !isDownButtonHidden"
            ref="arrowDown"
            class="devmode__arrow devmode__arrow_down"
            @click="clickDown"
        > 
            <common-icon :icon-name="'IconArrow'" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Strings } from "@/locales/types/enums/strings";
    import { type DebugSettings } from '@ca.vmedia.tv/domains';
    import { useApplicationStore } from "@/stores/Application";
    import { type IDevModeSettingsList, type TDevModeSettingsValue, listItems as defaultListItems } from "@/common/types/dev-mode";
    import { linkItems } from "@/components/Header/types";
    import { useDevModeState, type IUseDevModeState } from "@/composables/devMode/useDevModeState"
    import { useDevModeSettings } from "@/composables/devMode/useDevModeSettings"
    import { useI18n } from "vue-i18n";
    import { useFocusedComponents } from '@/composables/useFocusedComponents';
    import { type Ref, ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
    import { storeToRefs } from 'pinia';

    
    const ApplicationStore = useApplicationStore();
    const {
        getIsModalReloadAppShow,
    } = storeToRefs(ApplicationStore);

    const { t } = useI18n();
    const emit = defineEmits(['ready', 'start']);
    const { $navigator, $router } = useNuxtApp();
    const { devMode: appConfigDevMode } = useAppConfig();
    const { deactivateDevMode, isEnabledDevMode, dataLoaded: devModeStateDataLoaded }: IUseDevModeState = useDevModeState();
    const { debugSetting, setSettings, saveSettings } = useDevModeSettings();
    const { setFocused, unsetFocused, outerElementRef } = useFocusedComponents('DevModeWidget');
    const resetTitle: string = t(Strings.DEVMODE_RESET_TITLE).toString();
    const resetDescription: string = t(Strings.DEVMODE_RESET_DESCRIPTION).toString();
    const resetButton: string = t(Strings.DEVMODE_RESET_BUTTON).toString();
    const isRegKeyListeners: Ref<boolean> = ref(false);
    const currentSection: Ref<number> = ref(0);
    const currentSectionItem: Ref<number> = ref(0);
    const showArrows: Ref<boolean> = ref(false);
    const isUpButtonHidden: Ref<boolean> = ref(false);
    const isDownButtonHidden: Ref<boolean> = ref(false);
    const scrollable: Ref<HTMLDivElement | null> = ref(null);
    const arrowUp: Ref<HTMLDivElement | null> = ref(null);
    const arrowDown: Ref<HTMLDivElement | null> = ref(null);

    const listItems = computed((): IDevModeSettingsList[] => {
        const availableSections = appConfigDevMode.sections;
        return defaultListItems.filter(({ key }) => availableSections.some((item: string) => item === key));
    });

    const setArrowsState = (): void => {
       void nextTick(() => {
            if (!scrollable.value) {
                return;
            }
            showArrows.value = scrollable.value.scrollHeight > scrollable.value?.clientHeight;
            isDownButtonHidden.value = scrollable.value?.scrollTop === scrollable.value?.scrollHeight - scrollable.value?.clientHeight;
            isUpButtonHidden.value = scrollable.value?.scrollTop === 0;
        });
    };
    const getId = (sectionKey: string, itemKey: string): string => {
        return `devmode-${sectionKey}-${itemKey}`;
    };
    const onFocus = (sectionIdx?: number, idx?: number): void => {
        currentSection.value = sectionIdx ?? currentSection.value;
        currentSectionItem.value = idx ?? currentSectionItem.value;
        registerKeyListeners();
    };
    const onBlur = (): void => {
        unregisterKeyListeners();
    };
    const onKeyLeft = (event: KeyboardEvent): void => {
        const element = event.target as HTMLElement;
        if (element.getAttribute("data-type") === "modal-button") {
            return;
        }
        void goToBack();
    };
    const registerKeyListeners = (): void => {
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:left", onKeyLeft);
            $navigator.on("keypress:back", () => void goToBack());
            $navigator.on("keypress:down", onKeyDown);
            $navigator.on("keypress:up", onKeyUp);
            isRegKeyListeners.value = true;
        }
    };
    const unregisterKeyListeners = (): void => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:left", onKeyLeft);
            $navigator.off("keypress:back", () => void goToBack());
            $navigator.off("keypress:down", onKeyDown);
            $navigator.off("keypress:up", onKeyUp);
            isRegKeyListeners.value = false;
        }
    };
    const setSelfFocused = (): void => {
        setFocused();
        focusToElement();
    };
    const unsetSelfFocused = (): void => {
        unregisterKeyListeners();
        unsetFocused();
    };
    const goToBack = async (): Promise<void> => {
        try {
            await saveSettings();
        } catch (_) {
            $router.go(-1);
        }
    };
    const isChecked = (key: keyof DebugSettings, value: TDevModeSettingsValue): boolean => {
        return debugSetting.value?.[key] === value;
    };
    const clickUp = (event: MouseEvent) => {
        arrowUp.value?.classList.add('devmode__arrow_focused');
        setTimeout(() => {
            onKeyUp(event);
            arrowUp.value?.classList.remove('devmode__arrow_focused');
        }, 100);
    };
    const clickDown = (event: MouseEvent) => {
        arrowDown.value?.classList.add('devmode__arrow_focused');
        setTimeout(() => {
            onKeyDown(event);
            arrowDown.value?.classList.remove('devmode__arrow_focused');
        }, 100);
    };
    const onKeyUp = (event: KeyboardEvent | MouseEvent | HTMLElement | null): void => {
        if (event instanceof KeyboardEvent && event.key === "ArrowUp") {
            event.stopPropagation();
        }
        const activeElementID = (document.activeElement as HTMLElement).id;
        if (activeElementID === getId('reset', 'reset')) {
            focusToElement();
            return;
        }
        if (currentSection.value === 0 && currentSectionItem.value === 0 && scrollable.value !== null) {
            scrollable.value.scrollTop = 0;
            return;
        } else if (currentSection.value > 0 && currentSectionItem.value === 0) {
            currentSection.value -= 1;
            currentSectionItem.value = listItems.value[currentSection.value].values.length - 1;
        } else if (currentSectionItem.value > 0) {
            currentSectionItem.value -= 1;
        }
    };
    const onKeyDown = (event: KeyboardEvent | MouseEvent | HTMLElement | null): void => {
        if (event instanceof KeyboardEvent && event.key === "ArrowDown") {
            event.stopPropagation();
        }
        const lastSectionIndex = listItems.value.length - 1;
        const currentSectionItemIndex = listItems.value[currentSection.value].values.length - 1;
        if (scrollable.value !== null && currentSection.value === lastSectionIndex && currentSectionItem.value === currentSectionItemIndex) {
            scrollable.value.scrollTop = scrollable.value.scrollHeight - scrollable.value.clientHeight;
            setArrowsState();
            document.getElementById(getId('reset', 'reset'))?.focus();
            return;
        } else if (currentSection.value < lastSectionIndex && currentSectionItem.value === currentSectionItemIndex) {
            currentSection.value += 1;
            currentSectionItem.value = 0;
        } else if (currentSectionItem.value < currentSectionItemIndex) {
            currentSectionItem.value += 1;
        }
    };
    const focusToElement = (): void => {
        const itemID = getId(listItems.value[currentSection.value]?.key, listItems.value[currentSection.value]?.values[currentSectionItem.value].value);
        document.getElementById(itemID)?.focus();
        setArrowsState();
    };
    const backToHome = () => {
        void $router.push(linkItems.home.routerPath);
    };
    const start = () => {
        emit("start");
    };

    start();
   
    watch(
        currentSection,
        () => {
            focusToElement();
        }
    );
    watch(
        currentSectionItem,
        () => {
            focusToElement();
        }
    );
    watch(
        getIsModalReloadAppShow,
        (value) => {
            if (!value) {
                void goToBack();
            }
        }
    );
    watch(
        devModeStateDataLoaded,
        (value) => {
            if (value && !isEnabledDevMode.value) {
                backToHome();
            }
        }
    );


    onMounted(() => {
        emit("ready");
        setSelfFocused();
    });

    onBeforeUnmount(() => {
        unsetSelfFocused();
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>
